(() => {
  /*
   * JOAN'S PATTERN PURCHASE PANEL
   *
   * Handles:
   * - PDF + Printed patterns
   * - Printed-only paper patterns
   * - View and size-range combinations
   * - PDF variants that use "All views" / "All sizes"
   * - Horizon's native add-to-cart form
   */

  if (window.__joansPatternPurchaseLoaded) {
    return;
  }

  window.__joansPatternPurchaseLoaded = true;

  function initialisePatternPanels(root = document) {
    const panels = [];

    if (
      root instanceof Element &&
      root.matches("[data-joans-pattern-purchase]")
    ) {
      panels.push(root);
    }

    if (typeof root.querySelectorAll === "function") {
      panels.push(
        ...root.querySelectorAll(
          "[data-joans-pattern-purchase]"
        )
      );
    }

    panels.forEach((panel) => {
      initialisePatternPanel(panel);
    });
  }

  function initialisePatternPanel(panel) {
    if (
      panel.dataset.joansPatternInitialised ===
      "true"
    ) {
      return;
    }

    const productForm = panel.closest("form");

    const productSection =
      panel.closest(".shopify-section") ||
      panel.closest("section") ||
      document;

    if (!productForm) {
      console.error(
        "Joan's: native pattern product form not found"
      );
      return;
    }

    const nativeVariantInput =
      productForm.querySelector(
        'input[name="id"]'
      );

    const nativeQuantityInput =
      productForm.querySelector(
        'input[name="quantity"]'
      );

    const nativeAddToCartButton =
      productForm.querySelector(
        'button[type="submit"][name="add"]'
      );

    const variantsScript = panel.querySelector(
      "[data-joans-pattern-variants]"
    );

    const optionButtons = Array.from(
      panel.querySelectorAll(
        "[data-joans-pattern-option]"
      )
    );

    const optionGroups = Array.from(
      panel.querySelectorAll(
        ".joans-pattern-purchase__group"
      )
    );

    const priceDisplay = panel.querySelector(
      "[data-joans-pattern-price]"
    );

    const comparePriceDisplay =
      panel.querySelector(
        "[data-joans-pattern-compare-price]"
      );

    const selectionDisplay = panel.querySelector(
      "[data-joans-pattern-selection]"
    );

    const deliveryDisplay = panel.querySelector(
      "[data-joans-pattern-delivery]"
    );

    const customAddToCartButton =
      panel.querySelector(
        "[data-joans-pattern-add-to-cart]"
      );

    if (
      !nativeVariantInput ||
      !nativeAddToCartButton ||
      !variantsScript ||
      !priceDisplay ||
      !selectionDisplay ||
      !deliveryDisplay ||
      !customAddToCartButton
    ) {
      console.error(
        "Joan's: pattern purchase panel data not found"
      );
      return;
    }

    let variants;

    try {
      variants = JSON.parse(
        variantsScript.textContent || "[]"
      );
    } catch (error) {
      console.error(
        "Joan's: pattern variant data could not be read",
        error
      );
      return;
    }

    if (
      !Array.isArray(variants) ||
      variants.length === 0
    ) {
      console.error(
        "Joan's: no pattern variants were found"
      );
      return;
    }

    const initialVariantId =
      Number.parseInt(
        panel.dataset.initialVariantId || "",
        10
      );

    let currentVariant =
      variants.find(
        (variant) =>
          Number(variant.id) === initialVariantId
      ) ||
      variants.find(
        (variant) => variant.available
      ) ||
      variants[0];

    let selectedOptions =
      Array.isArray(currentVariant.options)
        ? [...currentVariant.options]
        : [];

    const currency =
      panel.dataset.currency || "NZD";

    const locale =
      panel.dataset.locale || "en-NZ";

    const formatIndex =
      Number.parseInt(
        panel.dataset.formatIndex || "0",
        10
      );

    /*
     * IMPORTANT:
     *
     * Do not assume "paper template" means paper-only.
     *
     * Mixed PDF + Printed products can use the same Joan's
     * pattern template. Instead, detect whether a real Format
     * group exists in the rendered picker.
     */

    const formatGroup = panel.querySelector(
      ".joans-pattern-purchase__group--format"
    );

    const hasFormatOption =
      Boolean(formatGroup);

    const templateMarkedPaperOnly =
      panel.dataset.paperOnly === "true";

    const trulyPaperOnly =
      templateMarkedPaperOnly &&
      !hasFormatOption;

    let isAddingToCart = false;

    const defaultAddToCartText =
      customAddToCartButton.textContent.trim() ||
      "Add to cart";

    panel.dataset.joansPatternInitialised =
      "true";

    productForm.classList.add(
      "joans-pattern-form--enhanced"
    );

    /*
     * Hide Horizon's native variant picker because our
     * custom Joan's picker is taking over.
     */

    const nativeVariantPicker =
      productSection.querySelector(
        "variant-picker"
      );

    if (nativeVariantPicker) {
      nativeVariantPicker.hidden = true;

      nativeVariantPicker.setAttribute(
        "aria-hidden",
        "true"
      );
    }

    /*
     * Patterns are sold one copy at a time.
     */

    if (nativeQuantityInput) {
      nativeQuantityInput.value = "1";
    }

    function formatMoney(cents) {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(
        Number(cents || 0) / 100
      );
    }

    function isPrintedFormat(formatValue) {
      const format = String(
        formatValue || ""
      ).toLowerCase();

      return (
        format.includes("printed") ||
        format.includes("paper")
      );
    }

    function displayFormatName(formatValue) {
      return isPrintedFormat(formatValue)
        ? "Printed"
        : "PDF";
    }

    function isPlaceholderOption(value) {
      const normalised = String(
        value || ""
      )
        .trim()
        .toLowerCase();

      return (
        normalised === "all views" ||
        normalised === "all sizes"
      );
    }

    function findVariant(options) {
      return variants.find((variant) => {
        if (
          !Array.isArray(variant.options)
        ) {
          return false;
        }

        return variant.options.every(
          (value, index) =>
            value === options[index]
        );
      });
    }

    function findVariantForFormat(
      formatValue
    ) {
      const matchingVariants =
        variants.filter(
          (variant) =>
            variant.options?.[
              formatIndex
            ] === formatValue
        );

      if (matchingVariants.length === 0) {
        return null;
      }

      /*
       * First try to preserve any existing view / size
       * selections if that combination actually exists.
       */

      const preservedOptions = [
        ...selectedOptions,
      ];

      preservedOptions[formatIndex] =
        formatValue;

      const exactMatch =
        matchingVariants.find(
          (variant) =>
            variant.options.every(
              (value, index) =>
                value ===
                preservedOptions[index]
            )
        );

      if (exactMatch) {
        return exactMatch;
      }

      /*
       * PDF normally has one variant:
       * PDF download / All views / All sizes.
       *
       * If switching from Printed to PDF, choose that
       * real PDF variant directly.
       */

      return (
        matchingVariants.find(
          (variant) => variant.available
        ) ||
        matchingVariants[0]
      );
    }

    function updateNativeProductForm(
      variant
    ) {
      /*
       * Set the actual variant ID that Horizon will submit.
       *
       * We deliberately do NOT dispatch Horizon's native
       * variant-change events here. They can re-render the
       * product section and reset the custom picker.
       */

      nativeVariantInput.value =
        String(variant.id);

      nativeAddToCartButton.disabled =
        !variant.available;

      nativeAddToCartButton.setAttribute(
        "aria-disabled",
        variant.available
          ? "false"
          : "true"
      );

      /*
       * Keep the URL variant parameter accurate.
       */

      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        "variant",
        String(variant.id)
      );

      window.history.replaceState(
        {},
        "",
        url.toString()
      );
    }

    function updateOptionButtons() {
      optionButtons.forEach((button) => {
        const optionPosition =
          Number.parseInt(
            button.dataset.optionPosition ||
              "",
            10
          );

        const optionIndex =
          optionPosition - 1;

        const optionValue =
          button.dataset.optionValue || "";

        const isSelected =
          selectedOptions[optionIndex] ===
          optionValue;

        button.classList.toggle(
          "is-selected",
          isSelected
        );

        button.setAttribute(
          "aria-pressed",
          isSelected
            ? "true"
            : "false"
        );

        let matchingVariant = null;

        /*
         * FORMAT BUTTONS
         *
         * PDF and Printed must remain clickable whenever
         * at least one variant exists for that format.
         *
         * Do NOT require the current View / Size combination
         * to exist in the new format.
         *
         * Example:
         *
         * Printed / Views 1 & 2 / 32–48
         *
         * can still switch to:
         *
         * PDF / All views / All sizes
         */

        if (
          hasFormatOption &&
          optionIndex === formatIndex
        ) {
          matchingVariant =
            variants.find(
              (variant) =>
                variant.options?.[
                  formatIndex
                ] === optionValue
            );
        } else {
          /*
           * For view / size buttons, check the exact
           * currently selected combination.
           */

          const candidateOptions = [
            ...selectedOptions,
          ];

          candidateOptions[
            optionIndex
          ] = optionValue;

          matchingVariant =
            findVariant(
              candidateOptions
            );
        }

        const combinationExists =
          Boolean(matchingVariant);

        /*
         * Only disable when that combination genuinely
         * does not exist.
         *
         * An unavailable variant remains selectable so
         * the customer can see that it is sold out.
         */

        button.disabled =
          !combinationExists;

        button.classList.toggle(
          "is-unavailable",
          Boolean(
            matchingVariant &&
              !matchingVariant.available
          )
        );
      });
    }

    function updateOptionGroups(
      formatValue
    ) {
      /*
       * Products without a Format choice are ordinary
       * paper-only products. Leave their other option
       * groups visible.
       */

      if (!hasFormatOption) {
        optionGroups.forEach(
          (group) => {
            group.hidden = false;
          }
        );

        return;
      }

      const showDependentOptions =
        isPrintedFormat(formatValue);

      optionGroups.forEach(
        (group) => {
          /*
           * Format itself always stays visible.
           */

          if (
            group.classList.contains(
              "joans-pattern-purchase__group--format"
            )
          ) {
            group.hidden = false;
            return;
          }

          /*
           * View and Size Range are only meaningful
           * for Printed.
           *
           * PDF's underlying "All views" / "All sizes"
           * values remain in Shopify but are hidden
           * from the customer-facing picker.
           */

          group.hidden =
            !showDependentOptions;
        }
      );
    }

    function getDisplaySelection(
      variant
    ) {
      if (!variant) {
        return "";
      }

      const formatValue =
        hasFormatOption
          ? variant.options?.[
              formatIndex
            ] || ""
          : "";

      const formatIsPrinted =
        hasFormatOption
          ? isPrintedFormat(
              formatValue
            )
          : trulyPaperOnly;

      const displayOptions =
        variant.options
          .map((value, index) => {
            if (
              hasFormatOption &&
              index === formatIndex
            ) {
              return displayFormatName(
                value
              );
            }

            return value;
          })
          .filter(
            (value, index) => {
              if (!value) {
                return false;
              }

              if (
                isPlaceholderOption(
                  value
                )
              ) {
                return false;
              }

              /*
               * PDF should display only "PDF".
               * Its All views / All sizes values
               * stay hidden.
               */

              if (
                hasFormatOption &&
                !formatIsPrinted &&
                index !== formatIndex
              ) {
                return false;
              }

              return true;
            }
          );

      return displayOptions.join(
        " · "
      );
    }

    function updatePanel() {
      currentVariant =
        findVariant(selectedOptions);

      updateOptionButtons();

      /*
       * If the selected options temporarily don't form
       * a real variant, show an unavailable state.
       */

      if (!currentVariant) {
        priceDisplay.textContent = "";

        if (comparePriceDisplay) {
          comparePriceDisplay.hidden =
            true;

          comparePriceDisplay.textContent =
            "";
        }

        const selectedFormatValue =
          hasFormatOption
            ? selectedOptions[
                formatIndex
              ] || ""
            : "";

        updateOptionGroups(
          selectedFormatValue
        );

        selectionDisplay.textContent =
          selectedOptions
            .map(
              (value, index) => {
                if (
                  hasFormatOption &&
                  index === formatIndex
                ) {
                  return displayFormatName(
                    value
                  );
                }

                return value;
              }
            )
            .filter(
              (value, index) => {
                if (!value) {
                  return false;
                }

                if (
                  isPlaceholderOption(
                    value
                  )
                ) {
                  return false;
                }

                if (
                  hasFormatOption &&
                  !isPrintedFormat(
                    selectedFormatValue
                  ) &&
                  index !==
                    formatIndex
                ) {
                  return false;
                }

                return true;
              }
            )
            .join(" · ");

        deliveryDisplay.textContent =
          "This combination is unavailable.";

        customAddToCartButton.disabled =
          true;

        customAddToCartButton.textContent =
          "Unavailable";

        return;
      }

      /*
       * PRICE
       */

      priceDisplay.textContent =
        formatMoney(
          currentVariant.price
        );

      if (
        comparePriceDisplay &&
        currentVariant.compare_at_price &&
        Number(
          currentVariant.compare_at_price
        ) >
          Number(
            currentVariant.price
          )
      ) {
        comparePriceDisplay.hidden =
          false;

        comparePriceDisplay.textContent =
          formatMoney(
            currentVariant.compare_at_price
          );
      } else if (
        comparePriceDisplay
      ) {
        comparePriceDisplay.hidden =
          true;

        comparePriceDisplay.textContent =
          "";
      }

      /*
       * FORMAT
       */

      const formatValue =
        hasFormatOption
          ? currentVariant.options?.[
              formatIndex
            ] || ""
          : "";

      updateOptionGroups(
        formatValue
      );

      /*
       * YOUR SELECTION
       */

      selectionDisplay.textContent =
        getDisplaySelection(
          currentVariant
        );

      /*
       * DELIVERY MESSAGE
       */

      if (trulyPaperOnly) {
        deliveryDisplay.textContent =
          "Physical paper pattern · shipping applies.";
      } else if (
        hasFormatOption &&
        isPrintedFormat(formatValue)
      ) {
        deliveryDisplay.textContent =
          "Full-scale pattern sheets posted from New Zealand. Shipping applies.";
      } else if (
        hasFormatOption
      ) {
        deliveryDisplay.textContent =
          "Digital pattern files available after checkout.";
      } else {
        deliveryDisplay.textContent =
          "Physical paper pattern · shipping applies.";
      }

      /*
       * ADD TO CART
       */

      customAddToCartButton.disabled =
        !currentVariant.available ||
        isAddingToCart;

      customAddToCartButton.textContent =
        currentVariant.available
          ? defaultAddToCartText
          : "Sold out";

      panel.dataset.currentVariantId =
        String(currentVariant.id);

      updateNativeProductForm(
        currentVariant
      );
    }

    function selectOption(button) {
      if (button.disabled) {
        return;
      }

      const optionPosition =
        Number.parseInt(
          button.dataset.optionPosition ||
            "",
          10
        );

      const optionIndex =
        optionPosition - 1;

      const optionValue =
        button.dataset.optionValue || "";

      if (
        Number.isNaN(optionIndex) ||
        optionIndex < 0
      ) {
        return;
      }

      /*
       * FORMAT SWITCH
       *
       * Handle this separately because PDF and Printed
       * intentionally do not share the same subordinate
       * option values.
       */

      if (
        hasFormatOption &&
        optionIndex === formatIndex
      ) {
        const matchingVariant =
          findVariantForFormat(
            optionValue
          );

        if (!matchingVariant) {
          return;
        }

        selectedOptions = [
          ...matchingVariant.options,
        ];

        updatePanel();

        return;
      }

      /*
       * VIEW / SIZE SWITCH
       */

      selectedOptions[
        optionIndex
      ] = optionValue;

      let matchingVariant =
        findVariant(
          selectedOptions
        );

      /*
       * If that exact combination doesn't exist,
       * stay inside the currently selected format
       * and find a real variant containing this
       * newly selected option.
       */

      if (!matchingVariant) {
        const selectedFormat =
          hasFormatOption
            ? selectedOptions[
                formatIndex
              ]
            : null;

        matchingVariant =
          variants.find(
            (variant) => {
              if (
                hasFormatOption &&
                variant.options?.[
                  formatIndex
                ] !== selectedFormat
              ) {
                return false;
              }

              return (
                variant.options?.[
                  optionIndex
                ] === optionValue &&
                variant.available
              );
            }
          ) ||
          variants.find(
            (variant) => {
              if (
                hasFormatOption &&
                variant.options?.[
                  formatIndex
                ] !== selectedFormat
              ) {
                return false;
              }

              return (
                variant.options?.[
                  optionIndex
                ] === optionValue
              );
            }
          );

        if (matchingVariant) {
          selectedOptions = [
            ...matchingVariant.options,
          ];
        }
      }

      updatePanel();
    }

    function setAddToCartLoading(
      isLoading
    ) {
      isAddingToCart = isLoading;

      customAddToCartButton.disabled =
        isLoading ||
        !currentVariant?.available;

      if (isLoading) {
        customAddToCartButton.textContent =
          "Adding…";
      } else {
        updatePanel();
      }
    }

    function addPatternToCart() {
      if (
        isAddingToCart ||
        !currentVariant ||
        !currentVariant.available
      ) {
        return;
      }

      /*
       * Make absolutely sure the correct Shopify
       * variant ID is in the native form before
       * triggering Horizon's button.
       */

      nativeVariantInput.value =
        String(currentVariant.id);

      if (nativeQuantityInput) {
        nativeQuantityInput.value = "1";
      }

      setAddToCartLoading(true);

      nativeAddToCartButton.click();

      window.setTimeout(() => {
        setAddToCartLoading(false);
      }, 1800);
    }

    /*
     * CUSTOM OPTION BUTTONS
     */

    optionButtons.forEach(
      (button) => {
        button.addEventListener(
          "click",
          () => {
            selectOption(button);
          }
        );
      }
    );

    /*
     * CUSTOM ADD TO CART
     */

    customAddToCartButton.addEventListener(
      "click",
      () => {
        addPatternToCart();
      }
    );

    /*
     * INITIAL RENDER
     */

    updatePanel();
  }

  /*
   * INITIAL PAGE LOAD
   */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initialisePatternPanels();
      }
    );
  } else {
    initialisePatternPanels();
  }

  /*
   * SHOPIFY THEME EDITOR
   */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initialisePatternPanels(
        event.target
      );
    }
  );
})();